import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Save, Plus, Trash2, Download, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const TEMPLATES = [
  { id: "officer", name: "HSE Officer", premium: false },
  { id: "engineer", name: "HSE Engineer", premium: true },
  { id: "senior", name: "Senior HSE Engineer", premium: true },
  { id: "manager", name: "HSE Manager", premium: true },
];

const empty = () => ({
  cv_id: null, title: "My HSE CV", template: "officer",
  full_name: "", job_title: "", email: "", phone: "", location: "",
  summary: "", skills: [], experience: [], education: [], certifications: [], trainings: [], projects: [],
});

export default function CVBuilder() {
  const { user } = useAuth();
  const [cv, setCv] = useState(empty());
  const [list, setList] = useState([]);

  useEffect(() => { api.get("/cv/list").then(r => setList(r.data.cvs)); }, []);

  const update = (k, v) => setCv(c => ({ ...c, [k]: v }));
  const addItem = (k, item) => setCv(c => ({ ...c, [k]: [...(c[k] || []), item] }));
  const rmItem = (k, i) => setCv(c => ({ ...c, [k]: c[k].filter((_, j) => j !== i) }));

  const save = async () => {
    try {
      const { data } = await api.post("/cv/save", cv);
      setCv(data.cv);
      const r = await api.get("/cv/list");
      setList(r.data.cvs);
      toast.success("CV saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const exportPdf = () => {
    // print-friendly export
    const w = window.open("", "cv-print");
    if (!w) return;
    w.document.write(`<html><head><title>${cv.full_name} CV</title>
      <style>body{font-family:Inter,sans-serif;color:#111;padding:32px;max-width:820px;margin:auto}
      h1{font-size:28px;margin:0}h2{font-size:16px;text-transform:uppercase;color:#b45309;margin:24px 0 8px;border-bottom:1px solid #eee;padding-bottom:4px}
      .meta{color:#555;margin-top:4px}.item{margin:8px 0}.role{font-weight:700}.small{color:#666;font-size:13px}</style></head><body>
      <h1>${cv.full_name || "Your Name"}</h1>
      <div class="meta">${cv.job_title || ""} · ${cv.location || ""} · ${cv.email || ""} · ${cv.phone || ""}</div>
      ${cv.summary ? `<h2>Summary</h2><div>${cv.summary}</div>` : ""}
      ${cv.skills?.length ? `<h2>Skills</h2><div>${cv.skills.join(" · ")}</div>` : ""}
      ${cv.experience?.length ? `<h2>Experience</h2>${cv.experience.map(e=>`<div class="item"><div class="role">${e.role || ""} — ${e.company || ""}</div><div class="small">${e.period || ""}</div><div>${e.desc || ""}</div></div>`).join("")}` : ""}
      ${cv.education?.length ? `<h2>Education</h2>${cv.education.map(e=>`<div class="item"><div class="role">${e.degree || ""} — ${e.institute || ""}</div><div class="small">${e.period || ""}</div></div>`).join("")}` : ""}
      ${cv.certifications?.length ? `<h2>Certifications</h2>${cv.certifications.map(c=>`<div class="item">${c.name || ""} · ${c.year || ""}</div>`).join("")}` : ""}
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="cv-builder-page">
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-extrabold tracking-tight">CV Builder</h1>
          <div className="flex gap-2">
            <button data-testid="cv-save" onClick={save} className="btn-primary !py-2 !px-4 text-sm"><Save className="h-4 w-4" /> Save</button>
            <button data-testid="cv-export" onClick={exportPdf} className="btn-outline !py-2 !px-4 text-sm"><Download className="h-4 w-4" /> Export</button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900 space-y-3">
          <div className="text-xs uppercase tracking-widest text-slate-500">Template</div>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map(t => {
              const locked = t.premium && !user?.is_premium;
              return (
                <button key={t.id} data-testid={`cv-template-${t.id}`} disabled={locked} onClick={() => update("template", t.id)}
                        className={`pill ${cv.template === t.id ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950" : "bg-slate-100 dark:bg-slate-800"} ${locked ? "opacity-50" : ""}`}>
                  {locked && <Lock className="h-3 w-3" />}{t.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input data-testid="cv-name" placeholder="Full Name" value={cv.full_name} onChange={(e)=>update("full_name", e.target.value)} />
            <Input data-testid="cv-title" placeholder="Job Title (e.g., HSE Officer)" value={cv.job_title} onChange={(e)=>update("job_title", e.target.value)} />
            <Input data-testid="cv-email" placeholder="Email" value={cv.email} onChange={(e)=>update("email", e.target.value)} />
            <Input data-testid="cv-phone" placeholder="Phone" value={cv.phone} onChange={(e)=>update("phone", e.target.value)} />
            <Input data-testid="cv-location" placeholder="Location" value={cv.location} onChange={(e)=>update("location", e.target.value)} />
          </div>
          <Textarea data-testid="cv-summary" placeholder="Professional summary…" rows={4} value={cv.summary} onChange={(e)=>update("summary", e.target.value)} />
          <Input data-testid="cv-skills" placeholder="Skills (comma separated)" value={cv.skills.join(", ")} onChange={(e)=>update("skills", e.target.value.split(",").map(s=>s.trim()).filter(Boolean))} />
        </div>

        {[
          { key: "experience", label: "Experience", fields: [{k:"role",p:"Role"},{k:"company",p:"Company"},{k:"period",p:"Period"},{k:"desc",p:"Description", full:true}] },
          { key: "education", label: "Education", fields: [{k:"degree",p:"Degree"},{k:"institute",p:"Institution"},{k:"period",p:"Period"}] },
          { key: "certifications", label: "Certifications", fields: [{k:"name",p:"Name"},{k:"year",p:"Year"}] },
        ].map(sec => (
          <div key={sec.key} className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold">{sec.label}</div>
              <button data-testid={`cv-add-${sec.key}`} onClick={() => addItem(sec.key, {})} className="btn-outline !py-1 !px-3 text-xs"><Plus className="h-3 w-3" /> Add</button>
            </div>
            {(cv[sec.key] || []).map((item, i) => (
              <div key={i} className="border border-slate-200 dark:border-slate-800 rounded-xl p-3 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {sec.fields.map(f => f.full ? (
                    <Textarea key={f.k} className="md:col-span-2" placeholder={f.p} rows={2} value={item[f.k] || ""} onChange={(e)=>{ const a=[...cv[sec.key]]; a[i]={...a[i],[f.k]:e.target.value}; update(sec.key, a); }} />
                  ) : (
                    <Input key={f.k} placeholder={f.p} value={item[f.k] || ""} onChange={(e)=>{ const a=[...cv[sec.key]]; a[i]={...a[i],[f.k]:e.target.value}; update(sec.key, a); }} />
                  ))}
                </div>
                <button onClick={() => rmItem(sec.key, i)} className="text-xs text-rose-500 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove</button>
              </div>
            ))}
          </div>
        ))}
      </div>

      <aside className="space-y-3">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900">
          <div className="text-xs uppercase tracking-widest text-slate-500">Preview</div>
          <div className="mt-2">
            <div className="text-xl font-bold">{cv.full_name || "Your Name"}</div>
            <div className="text-xs text-slate-500">{cv.job_title || "Your role"}</div>
            <div className="text-xs text-slate-500">{cv.location} {cv.email && "· " + cv.email}</div>
            <p className="mt-3 text-sm">{cv.summary || "Your professional summary appears here."}</p>
            {cv.skills.length > 0 && <div className="mt-3 flex flex-wrap gap-1">{cv.skills.map((s,i)=>(<span key={i} className="pill bg-slate-100 dark:bg-slate-800">{s}</span>))}</div>}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900">
          <div className="text-xs uppercase tracking-widest text-slate-500">My CVs</div>
          {list.length === 0 ? <div className="text-sm text-slate-500 mt-2">No saved CVs.</div> :
            <ul className="mt-2 space-y-1">{list.map(c => (
              <li key={c.cv_id}><button className="text-sm text-amber-600" onClick={()=>setCv(c)}>{c.title}</button></li>
            ))}</ul>}
        </div>
        {!user?.is_premium && (
          <Link to="/subscription" className="block rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
            <div className="font-semibold">Unlock premium templates</div>
            <div className="text-slate-600 dark:text-slate-300">Engineer, Senior & Manager templates included.</div>
          </Link>
        )}
      </aside>
    </div>
  );
}
